"use client";

import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, ExternalLink, TrendingUp, TrendingDown, Minus, AlarmClock, Inbox, MoveHorizontal } from "lucide-react";

import ArticleThumbnail from "@/components/ArticleThumbnail";
import BriefIcon from "@/components/BriefIcon";

const READING_WIDTHS = {
  narrow: { label: "좁게", className: "max-w-3xl" },
  medium: { label: "중간", className: "max-w-6xl" },
  full: { label: "가득", className: "max-w-none" },
} as const;
type ReadingWidth = keyof typeof READING_WIDTHS;
const WIDTH_STORAGE_KEY = "brief-reading-width";

interface MarketItem {
  code: "kospi" | "kosdaq" | "usdkrw";
  label: string;
  value: number;
  change: number;
  changePct: number;
  direction: "up" | "down" | "flat";
}

interface SectionItem {
  thumbnailKey?: string | null;
  text: string;
  source: string | null;
  url: string | null;
  emphasis: "bold" | "warn" | "info" | null;
}

interface Section {
  key: string;
  icon: string;
  title: string;
  order: number;
  items: SectionItem[];
}

interface InteractionBrief {
  date: string;
  weekday: string;
  title: string;
  intro: { body: string };
  marketSnapshot: {
    asOf: string;
    asOfLabel: string;
    items: MarketItem[];
  };
  threeLineSummary: string[];
  sections: Section[];
  meta: {
    generatedAt: string;
    articlesUsed: number;
    sources: string[];
    articleUrlsUsed: string[];
    pipelineVersion: string;
  };
}

const fmt = (n: number, digits = 2) =>
  n.toLocaleString("ko-KR", { minimumFractionDigits: digits, maximumFractionDigits: digits });

function DirectionIcon({ direction }: { direction: MarketItem["direction"] }) {
  if (direction === "up") return <TrendingUp className="w-4 h-4 text-red-500" />;
  if (direction === "down") return <TrendingDown className="w-4 h-4 text-blue-500" />;
  return <Minus className="w-4 h-4 text-gray-400" />;
}

function MarketCard({ item }: { item: MarketItem }) {
  const color =
    item.direction === "up"
      ? "text-red-600"
      : item.direction === "down"
        ? "text-blue-600"
        : "text-gray-500";
  const sign = item.change > 0 ? "+" : item.change < 0 ? "" : "";
  const pctSign = item.changePct > 0 ? "+" : item.changePct < 0 ? "" : "";
  const isRate = item.code === "usdkrw";
  return (
    <div className="flex-1 min-w-[180px] rounded-2xl bg-white/80 dark:bg-zinc-900/60 backdrop-blur px-5 py-4 border border-zinc-200/60 dark:border-zinc-800/80">
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="text-xs font-semibold tracking-wide text-zinc-500 uppercase">
          {item.label}
        </span>
        <DirectionIcon direction={item.direction} />
      </div>
      <div className="text-2xl font-bold tabular-nums">
        {fmt(item.value, isRate ? 2 : 2)}
      </div>
      <div className={`mt-1 text-sm font-medium tabular-nums ${color}`}>
        {sign}{fmt(item.change, 2)}{" "}
        <span className="opacity-80">
          ({pctSign}{fmt(item.changePct, 2)}%)
        </span>
      </div>
    </div>
  );
}

function InlineMd({ text }: { text: string }) {
  // Parse **bold**, *italic*, and `code` inline. Minimal; trusts subagent output.
  const tokens = text.split(/(\*\*[^*\n]+\*\*|`[^`\n]+`|\*[^*\n]+\*)/g);
  return (
    <>
      {tokens.map((tok, i) => {
        if (tok.startsWith("**") && tok.endsWith("**")) {
          return <strong key={i} className="font-semibold text-zinc-900 dark:text-zinc-100">{tok.slice(2, -2)}</strong>;
        }
        if (tok.startsWith("`") && tok.endsWith("`")) {
          return <code key={i} className="px-1 py-0.5 rounded bg-zinc-100 dark:bg-zinc-800 font-mono text-xs">{tok.slice(1, -1)}</code>;
        }
        if (tok.startsWith("*") && tok.endsWith("*") && tok.length > 2) {
          return <em key={i}>{tok.slice(1, -1)}</em>;
        }
        return <span key={i}>{tok}</span>;
      })}
    </>
  );
}

function SectionBlock({ section }: { section: Section }) {
  if (!section.items?.length) return null;
  return (
    <section className="rounded-2xl bg-white/70 dark:bg-zinc-900/50 backdrop-blur border border-zinc-200/60 dark:border-zinc-800/80 p-5 md:p-6">
      <h3 className="flex items-center gap-2 text-lg font-semibold mb-3 text-zinc-900 dark:text-zinc-100">
        <BriefIcon value={section.icon} className="w-6 h-6 shrink-0" />
        <span>{section.title}</span>
      </h3>
      <ul className="space-y-2.5">
        {section.items.map((it, idx) => (
          <li key={idx} className="flex flex-wrap sm:flex-nowrap gap-3 text-base leading-[1.8] text-zinc-700 dark:text-zinc-300">
            <ArticleThumbnail imageKey={it.thumbnailKey} href={it.url} source={it.source} className="w-full sm:w-40 sm:shrink-0" />
            <span className="text-zinc-400 dark:text-zinc-600 select-none mt-0.5">•</span>
            <div className="flex-1 min-w-0">
              <InlineMd text={it.text} />
              {it.url && (
                <a
                  href={it.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-2 inline-flex items-center gap-0.5 text-xs text-blue-600 dark:text-blue-400 hover:underline align-middle"
                >
                  {it.source || "출처"}
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

export default function NewsPage() {
  const [readingWidth, setReadingWidth] = useState<ReadingWidth>(() => {
    try {
      const saved = typeof window === "undefined" ? null : localStorage.getItem(WIDTH_STORAGE_KEY);
      return saved === "medium" || saved === "full" ? saved : "narrow";
    } catch { return "narrow"; }
  });
  const changeWidth = (width: ReadingWidth) => {
    setReadingWidth(width);
    try { localStorage.setItem(WIDTH_STORAGE_KEY, width); } catch { /* Still usable without persistent storage. */ }
  };
  const [data, setData] = useState<InteractionBrief | null>(null);
  const [dates, setDates] = useState<string[]>([]);
  const [currentDate, setCurrentDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = (date?: string) => {
    const url = date ? `/api/ibrief?date=${date}` : "/api/ibrief";
    fetch(url)
      .then(r => r.json())
      .then(res => {
        if (res.error) {
          setError(res.error);
          setLoading(false);
          return;
        }
        setData(res.data);
        setDates(res.availableDates || []);
        setCurrentDate(res.currentDate || "");
        setLoading(false);
      })
      .catch(e => {
        setError(String(e));
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchData();
  }, []);

  const navigateDate = (direction: number) => {
    const idx = dates.indexOf(currentDate);
    const newIdx = idx + direction;
    if (newIdx >= 0 && newIdx < dates.length) {
      setLoading(true);
      setError(null);
      fetchData(dates[newIdx]);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-zinc-500">
        로딩 중…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-zinc-500 p-6">
        <Inbox className="mb-4 h-8 w-8" aria-hidden="true" /><div className="text-lg mb-2">아직 발행된 Interaction Brief가 없어요</div>
        {error && <div className="text-xs opacity-60">{error}</div>}
      </div>
    );
  }

  const currIdx = dates.indexOf(currentDate);
  const hasPrev = currIdx < dates.length - 1; // older
  const hasNext = currIdx > 0; // newer

  return (
    <div className="min-h-screen">
      <div className={`w-full ${READING_WIDTHS[readingWidth].className} mx-auto px-4 md:px-6 py-8 md:py-12`} data-reading-width={readingWidth}>
        {/* Header + date nav */}
        <header className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <button
              onClick={() => navigateDate(1)}
              disabled={!hasPrev}
              className="p-2 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800 disabled:opacity-30"
              aria-label="이전 Interaction Brief"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="text-center">
              <div className="text-xs uppercase tracking-widest text-zinc-400">
                brief.interaction.hnine.com · interaction brief
              </div>
              <h1 className="text-2xl md:text-3xl font-bold mt-1 text-zinc-900 dark:text-zinc-100">
                {data.title || `${data.date} Interaction Brief`}
              </h1>
            </div>
            <button
              onClick={() => navigateDate(-1)}
              disabled={!hasNext}
              className="p-2 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800 disabled:opacity-30"
              aria-label="다음 Interaction Brief"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
            <span className="inline-flex items-center gap-1.5 text-xs text-zinc-500 dark:text-zinc-400"><MoveHorizontal size={16} aria-hidden="true" />본문 폭</span>
            <div role="group" aria-label="브리프 페이지 넓이" className="inline-flex gap-1 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white/80 dark:bg-zinc-900/80 p-1">
              {(Object.keys(READING_WIDTHS) as ReadingWidth[]).map(width => (
                <button key={width} type="button" aria-pressed={readingWidth === width} onClick={() => changeWidth(width)} className={`min-h-10 min-w-14 rounded-lg px-3 text-xs font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500 ${readingWidth === width ? "bg-indigo-600 text-white" : "text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800"}`}>
                  {READING_WIDTHS[width].label}
                </button>
              ))}
            </div>
          </div>
        </header>

        {/* Market snapshot */}
        {data.marketSnapshot?.items?.length > 0 && (
          <div className="mb-5">
            <div className="flex gap-3 flex-wrap">
              {data.marketSnapshot.items.map(it => (
                <MarketCard key={it.code} item={it} />
              ))}
            </div>
            <div className="mt-2 text-xs text-zinc-500 dark:text-zinc-400 text-right">
              {data.marketSnapshot.asOfLabel}
            </div>
          </div>
        )}

        {/* Intro */}
        {data.intro?.body && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="mb-6 rounded-2xl bg-amber-50/50 dark:bg-amber-900/10 border border-amber-200/50 dark:border-amber-900/30 p-5 md:p-6"
          >
            <p className="text-base leading-[1.8] text-zinc-700 dark:text-zinc-200 whitespace-pre-wrap">
              {data.intro.body}
            </p>
          </motion.div>
        )}

        {/* Three-line summary */}
        {data.threeLineSummary?.length > 0 && (
          <section className="mb-6 rounded-2xl bg-white/80 dark:bg-zinc-900/60 backdrop-blur border border-zinc-200/60 dark:border-zinc-800/80 p-5 md:p-6">
            <h2 className="flex items-center gap-2 text-base font-semibold mb-3 text-zinc-900 dark:text-zinc-100">
              <AlarmClock className="w-5 h-5 shrink-0" aria-hidden="true" />
              <span>오늘의 Interaction Brief 세 줄 요약</span>
            </h2>
            <ul className="space-y-2">
              {data.threeLineSummary.map((line, i) => (
                <li key={i} className="flex gap-2 text-base leading-[1.8] text-zinc-700 dark:text-zinc-300">
                  <span className="font-semibold text-amber-600 dark:text-amber-400 shrink-0">{i + 1}.</span>
                  <span><InlineMd text={line} /></span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Sections */}
        <AnimatePresence mode="wait">
          <motion.div
            key={data.date}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="space-y-4"
          >
            {data.sections
              .sort((a, b) => a.order - b.order)
              .map(s => (
                <SectionBlock key={s.key} section={s} />
              ))}
          </motion.div>
        </AnimatePresence>

        {/* Footer meta */}
        <footer className="mt-8 pt-6 border-t border-zinc-200/50 dark:border-zinc-800/50 text-xs text-zinc-500 dark:text-zinc-400 text-center">
          Interaction Brief · {data.date} · 기사 {data.meta?.articlesUsed || 0}건 참조
          {data.meta?.pipelineVersion ? ` · ${data.meta.pipelineVersion}` : ""}
        </footer>
      </div>
    </div>
  );
}
